import { Info } from '@phosphor-icons/react'
import ReactTooltip from 'react-tooltip'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'
import { startOfDay, endOfDay, subMinutes, addMinutes } from 'date-fns'

import TextField from '../../../../FormLegacy/FormFieldsV2/TextField'
import SelectField from '../../../../FormLegacy/FormFieldsV2/SelectField'
import ToggleField from '../../../../FormLegacy/FormFieldsV2/ToggleField'
import HourlyPricingSelector from '../components/HourlyPricingSelector'
import MultidayPricingSelector from '../components/MultidayPricingSelector'
import DateField from '../../../../FormLegacy/FormFieldsV2/DateField'
import {
  getTimeIntervalOptions,
  onChangeNumberField,
  dateChangeRaw,
  DEFAULT_TIME_INTERVAL,
  MAX_FEE_NAME_LENGTH,
} from '../helpers'
import SpecialPricingSection from '../components/SpecialPricingSection'

const PricingAndAvailability = ({
  values,
  setFieldValue = () => {},
  handleChange = () => {},
  errors = {},
  resetKey = 0,
}) => {
  const { t } = useTranslation(['reservations'])
  const updateReservationInterval = (e) => {
    const value = e.target.value

    if (values.minTime) {
      setFieldValue('minTime', value)

      if (values.maxTime < value) {
        setFieldValue('maxTime', value)
      } else if (values.maxTime % value !== 0) {
        const adjustedMaxTime = Math.ceil(values.maxTime / value) * value
        setFieldValue('maxTime', adjustedMaxTime)
      }
    }

    setFieldValue('hourlySlots', [])
    setFieldValue('daysAvailable', [])

    handleChange(e)
  }

  const onChangeCheckInTime = (e) => {
    const value = e.target.value
    setFieldValue('checkInTime', value)
    setFieldValue('checkOutTime', undefined)
  }

  return (
    <div className="form-step">
      <div className="form-step__content">
        <div className="form-step__section">
          <div className="form-step__row">
            {values.multiDayBooking ? (
              <>
                <div className="form-step__column">
                  <DateField
                    name="checkInTime"
                    label={t('reservations:amenityForms.pricing.checkInTimeRequired')}
                    placeholderText={t('reservations:shared.selectTimePlaceholder')}
                    value={values.checkInTime}
                    onChange={onChangeCheckInTime}
                    minTime={addMinutes(startOfDay(new Date()), DEFAULT_TIME_INTERVAL)}
                    maxTime={endOfDay(new Date())}
                    showTimeSelect
                    showTimeSelectOnly
                    timeIntervals={DEFAULT_TIME_INTERVAL}
                    timeCaption={t('reservations:multiReservationAmenityForm.checkInTime')}
                    dateFormat="hh:mm a"
                    onChangeRaw={dateChangeRaw}
                    isError={errors.checkInTime}
                  />
                </div>
                <div className="form-step__column">
                  <DateField
                    name="checkOutTime"
                    label={t('reservations:amenityForms.pricing.checkOutTimeRequired')}
                    placeholderText={t('reservations:shared.selectTimePlaceholder')}
                    value={values.checkOutTime}
                    onChange={handleChange}
                    minTime={startOfDay(new Date())}
                    maxTime={
                      values.checkInTime
                        ? subMinutes(values.checkInTime, DEFAULT_TIME_INTERVAL)
                        : endOfDay(new Date())
                    }
                    showTimeSelect
                    showTimeSelectOnly
                    timeIntervals={DEFAULT_TIME_INTERVAL}
                    timeCaption={t('reservations:multiReservationAmenityForm.time')}
                    dateFormat="hh:mm a"
                    onChangeRaw={dateChangeRaw}
                    isError={errors.checkOutTime}
                  />
                </div>
              </>
            ) : (
              <div className="form-step__column">
                <div className="form-step__label">
                  {t('reservations:amenityForms.pricing.reservationTimeIntervalRequired')}
                  <Info size={16} data-tip data-for="reservationTimeInterval" />
                  <ReactTooltip
                    className="tooltip"
                    multiline={true}
                    id="reservationTimeInterval"
                    place="top"
                    effect="solid"
                  >
                    {t('reservations:amenityForms.pricing.reservationTimeIntervalTooltip')}
                  </ReactTooltip>
                </div>
                <SelectField
                  name="timeInterval"
                  value={values.timeInterval}
                  options={getTimeIntervalOptions(t)}
                  onChange={updateReservationInterval}
                  isError={errors.timeInterval}
                />
              </div>
            )}
          </div>
          <div className="form-step__column">
            {values.multiDayBooking ? (
              <MultidayPricingSelector
                key={`multiday-${resetKey}`}
                resetKey={resetKey}
                values={values}
                errors={errors}
                handleChange={handleChange}
                setFieldValue={setFieldValue}
              />
            ) : (
              <HourlyPricingSelector
                key={`hourly-${resetKey}-${values.timeInterval}`}
                resetKey={resetKey}
                values={values}
                errors={errors}
                handleChange={handleChange}
                setFieldValue={setFieldValue}
              />
            )}
          </div>
        </div>

        <div className="form-step__section">
          {!values.multiDayBooking && (
            <>
              <div className="form-step__row form-step__row--between">
                <div className="form-step__column">
                  <DateField
                    name="startDate"
                    label={t('reservations:amenityForms.pricing.startDate')}
                    placeholderText="MM/DD/YYYY"
                    value={values.startDate}
                    onChange={handleChange}
                    minDate={startOfDay(new Date())}
                    maxDate={values.endDate ? endOfDay(values.endDate) : undefined}
                    isError={!!errors.startDate}
                  />
                </div>
                <div className="form-step__column">
                  <DateField
                    name="endDate"
                    label={t('reservations:amenityForms.pricing.endDate')}
                    placeholderText="MM/DD/YYYY"
                    value={values.endDate}
                    onChange={handleChange}
                    minDate={startOfDay(values.startDate || new Date())}
                    isError={!!errors.endDate}
                  />
                </div>
              </div>
              <div className="form-step__divider" />
            </>
          )}
          <div className="form-step__row form-step__row--between">
            <div>
              <div className="form-step__label">
                {t('reservations:amenityForms.pricing.customFee')}
                <Info size={16} data-tip data-for="customFee" />
                <ReactTooltip
                  className="tooltip"
                  multiline={true}
                  id="customFee"
                  place="top"
                  effect="solid"
                >
                  {t('reservations:amenityForms.pricing.customFeeTooltip')}
                </ReactTooltip>
              </div>
            </div>
            <div>
              <ToggleField
                name="extraFeeEnabled"
                label={
                  values.extraFeeEnabled
                    ? t('reservations:amenityForms.yes')
                    : t('reservations:amenityForms.no')
                }
                value={values.extraFeeEnabled}
                onChange={handleChange}
                labelOnRight
              />
            </div>
          </div>
          {values.extraFeeEnabled && (
            <div className="form-step__row form-step__row--between">
              <div className="form-step__column">
                <TextField
                  label={t('reservations:amenityForms.pricing.feeNameRequired')}
                  name="extraFeeName"
                  placeholder={t('reservations:singleReservationAmenityForm.oneTimeFeeName')}
                  value={values.extraFeeName}
                  onChange={handleChange}
                  isError={errors.extraFeeName}
                  maxLength={MAX_FEE_NAME_LENGTH}
                />
              </div>
              <div className="form-step__column">
                <TextField
                  label={t('reservations:amenityForms.pricing.feeAmountRequired')}
                  name="extraFee"
                  placeholder={t('reservations:amenityForms.pricing.enterFeeAmountPlaceholder')}
                  value={values.extraFee}
                  onChange={(e) => onChangeNumberField(e, handleChange)}
                  type="number"
                  min={0}
                  isError={errors.extraFee}
                />
              </div>
            </div>
          )}
          <div className="form-step__divider" />

          <SpecialPricingSection
            slots={values.specialPricingSlots}
            specialPricing={values.specialPricing}
            handleChange={handleChange}
            setFieldValue={setFieldValue}
            error={errors.specialPricingSlots}
          />
        </div>
      </div>
    </div>
  )
}

PricingAndAvailability.propTypes = {
  values: PropTypes.object.isRequired,
  setFieldValue: PropTypes.func.isRequired,
  handleChange: PropTypes.func.isRequired,
  errors: PropTypes.object,
  touched: PropTypes.object,
  setFieldTouched: PropTypes.func,
  resetKey: PropTypes.number,
}

export default PricingAndAvailability
